import type { TourStep } from '@/components/ui/Tour';
import { CAMP_BULK_WIZARD_TOUR_STEP_IDS } from '../tour-constants';
import { TourStepContent } from './StepContent';

export const CAMP_BULK_WIZARD_STEP_COUNT = 7;

const STEP_CONTENT: Record<
  number,
  { title: string; description: string }
> = {
  1: {
    title: 'Shared camp name',
    description:
      'Enter the base name every camp in this batch will share. Each instance becomes "{name} Week N", with an optional variant suffix when you split by program or age group. Add a default description families see on every camp unless you override it later.',
  },
  3: {
    title: 'Split variants',
    description:
      'Keep one camp per week, or split into separate camps per development program or age group. Program splits can prefill sport, skill level, description, and cover image from each program.',
  },
  4: {
    title: 'Preview the batch',
    description:
      'Confirm how many camps will be created and skim the generated names before you set pricing and staffing defaults.',
  },
  6: {
    title: 'Per-instance overrides',
    description:
      'Expand any row to tweak coaches, courts, pricing, description, or cover image for one camp only. Leave rows collapsed to keep the defaults from the previous step.',
  },
  7: {
    title: 'Review and create',
    description:
      'Double-check schedule, split, pricing, and staffing. When everything looks right, create all camps in one batch — you can edit individual camps afterward from Camp Management.',
  },
};

const SCHEDULE_TOUR_STEPS: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Start date and weeks"
        description="Choose when week 1 begins and how many weekly camps to create. Each following week starts seven days after the previous one."
      />
    ),
    selectorId: CAMP_BULK_WIZARD_TOUR_STEP_IDS.SCHEDULE_SPAN,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Times and days"
        description="Set the daily start and end times, then select which weekdays sessions run. Use the preview list to confirm the date range for each generated week."
      />
    ),
    selectorId: CAMP_BULK_WIZARD_TOUR_STEP_IDS.SCHEDULE_TIMES_DAYS,
    position: 'top',
  },
];

const DEFAULTS_TOUR_STEPS: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Defaults for every camp"
        description="These settings apply to the whole batch — each generated camp inherits them until you override a specific instance on the next step. Pricing and capacity are required so families see correct book-by-day and full-camp amounts and enrollment limits."
      />
    ),
    selectorId: CAMP_BULK_WIZARD_TOUR_STEP_IDS.DEFAULTS_SHARED,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Optional details"
        description="Skill level, location, coaches, courts, and cover image are not required to create camps. Add them when they help scheduling, court assignment, or your public camp page — or leave them blank and fill in later, including per-instance overrides."
      />
    ),
    selectorId: CAMP_BULK_WIZARD_TOUR_STEP_IDS.DEFAULTS_OPTIONAL,
    position: 'top',
  },
];

/** Guided steps for the current wizard screen. */
export function getCampBulkWizardTourSteps(wizardStep: number): TourStep[] {
  if (wizardStep === 2) {
    return SCHEDULE_TOUR_STEPS;
  }

  if (wizardStep === 5) {
    return DEFAULTS_TOUR_STEPS;
  }

  const content = STEP_CONTENT[wizardStep];
  if (!content) return [];

  return [
    {
      content: <TourStepContent title={content.title} description={content.description} />,
      selectorId: CAMP_BULK_WIZARD_TOUR_STEP_IDS.contentId(wizardStep),
      position: 'top',
    },
  ];
}
